<script>

export default {
  name: 'ToolbarSearch',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    showType: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: ''
    },
    total: {
      type: [Number, null],
      default: null
    },
    /** Contextualizes the type of data being displayed on the counter  */
    type: {
      type: String,
      default: 'vehicle'
    }
  },
  data: () => ({
    label: 'Quick Search ("/" to focus)',
    isSearchBarMode: false
  }),
  computed: {
    searchTerm: {
      get: ({ modelValue }) => modelValue,
      set (newVal) {
        if (!newVal) return this.$emit('update:modelValue', newVal)

        // remove ascii chars outside of hex code range
        // from space to delete and from À to ÿ
        // https://en.wikipedia.org/wiki/ASCII#Character_set
        newVal = newVal.replace(/[^\x20-\x7F\xC0-\xFF]/g, '')
        // remove unwanted chars from text field internals

        this.$refs.search.lazyValue = newVal
        this.$emit('update:modelValue', newVal)
      }
    },
    showSearchBox () {
      return this.$vuetify.display.mdAndUp
    }
  },
  mounted () {
    document.onkeydown = this.keyboardShortcut.bind(this)
  },
  beforeUnmount () {
    document.onkeydown = null
  },
  methods: {
    async showSearchBar () {
      this.isSearchBarMode = true
      await nextTick()
      this.$refs.search.focus()
    },

    keyboardShortcut ($event) {
      if (!this.$refs.search || !this.showSearchBox) return
      const { key, target } = $event
      const isFocused = (target === this.$refs.search.$el.querySelector('input'))

      if (isFocused && (key === 'Escape' || key === 'Enter')) {
        if (key === 'Escape') this.searchTerm = ''
        document.activeElement.blur()
        return
      }

      if (key !== '/' || target.nodeName === 'INPUT') return
      $event.preventDefault()
      this.$refs.search.focus()
    }
  }
}
</script>

<template>
  <VToolbar
    class="v-toolbar--search"
    flat
  >
    <template v-if="!isSearchBarMode">
      <div class="d-flex align-center">
        <VToolbarTitle
          v-if="title"
          class="pl-4"
        >
          {{ title }}
        </VToolbarTitle>
        <VListSubheader
          v-if="total !== null && showType"
          class="ml-6"
        >
          {{ $filters.number(total) }} {{ type + (total === 1 ? '' : 's') }}
        </VListSubheader>
      </div>
      <VSpacer />
      <VCol
        cols="auto"
        md="7"
        class="pa-0"
      >
        <VTextField
          v-if="showSearchBox"
          ref="search"
          v-model="searchTerm"
          :label="label"
          clearable
          variant="solo"
          hide-details
          prepend-inner-icon="$search"
          bg-color="grey-lighten-3"
          density="comfortable"
          single-line
          flat
        />
      </VCol>
      <VSpacer />
      <div class="v-toolbar--search--action-items d-flex">
        <VTooltip
          v-if="!showSearchBox"
          class="v-toolbar--search--action-items--search"
          location="bottom"
        >
          <template #activator="{ props }">
            <VBadge
              dot
              offset-x="10"
              offset-y="11"
              :model-value="Boolean(searchTerm)"
              color="primary"
            >
              <VBtn
                icon
                v-bind="props"
                color="grey-darken-1"
                @click="showSearchBar"
              >
                <VIcon>$search</VIcon>
              </VBtn>
            </VBadge>
          </template>
          <span>Search</span>
        </VTooltip>
        <slot name="actionItems" />
      </div>
    </template>
    <template v-if="isSearchBarMode">
      <VCol
        cols="12"
      >
        <div class="d-flex">
          <VTooltip
            class="v-toolbar--search--back"
            location="bottom"
          >
            <template #activator="{ props }">
              <VBtn
                icon
                class="mt-3"
                v-bind="props"
                color="grey-darken-1"
                @click="isSearchBarMode = false"
              >
                <VIcon>$arrowBack</VIcon>
              </VBtn>
            </template>
            <span>Back</span>
          </VTooltip>
          <div class="v-toolbar--search--barmode pa-0 d-flex justify-center">
            <VCol
              cols="7"
            >
              <VTextField
                ref="search"
                v-model="searchTerm"
                label="Search your inventory"
                clearable
                variant="solo"
                hide-details
                prepend-inner-icon="$search"
                density="comfortable"
                bg-color="grey-lighten-3"
                single-line
                flat
              />
            </VCol>
          </div>
        </div>
      </VCol>
    </template>
    <VProgressLinear
      :active="loading"
      indeterminate
      absolute
      location="bottom"
    />
  </VToolbar>
</template>

<style lang="scss">
  @use '~/styles/mixins' as *;

  .v-toolbar--search--action-items {
    align-items: center;
    display: flex;
  }

  .v-toolbar.v-toolbar--search {
    @include z-index(primary-menu);

    border-bottom: 1px solid #DADCE0;
    position: sticky;
    top: -1px;
    white-space: nowrap;

    .v-toolbar__content {
      .v-toolbar-title {
        text-overflow: unset;
      }

      .v-list-subheader {
        min-width: 120px;
      }
    }

    .v-toolbar--search--barmode {
      width: 100%;
    }

    .v-field--focused {
      box-shadow: 0 3px 1px -2px var(--v-shadow-key-umbra-opacity, rgba(0 0 0 / 20%)), 0 2px 2px 0 var(--v-shadow-key-penumbra-opacity, rgba(0 0 0 / 14%)), 0 1px 5px 0 var(--v-shadow-key-ambient-opacity, rgba(0 0 0 / 12%));

      .v-field__overlay {
        background-color: #fff !important;
      }

      .v-icon__svg {
        fill: rgb(var(--v-theme-primary));
      }

      .v-field__prepend-inner > .v-icon,
      .v-field__append-inner > .v-icon,
      .v-field__clearable > .v-icon {
        opacity: unset;
      }
    }

  }
</style>
